import type { FiuuTopUpStart } from './fiuu-topup.js';
import { normaliseFiuuTopUpStart } from './fiuu-topup.js';
import type { LegacyApiClient } from './legacy-api/client.js';

/**
 * Starting a wallet top-up (MYP-134): `molpay_pay`, from the vendor's guide of 2026-10-26. It
 * reads the answer through `normaliseFiuuTopUpStart`, so what comes out is either a checked link
 * or a `FiuuTopUpError` — never a raw answer, and never a link nobody has checked.
 *
 * How the top-up ended is read from `get_trade` (`findFiuuTopUpState`), not here: the vendor has no
 * status endpoint of its own.
 *
 * It takes the client as an argument, rather than importing the app's, so it can be tested here
 * with a fake `fetch` — `apps/web` has no test runner.
 */

/**
 * Whole cents → the amount as the vendor receives it, always with two decimals: `10000` →
 * `"100.00"`, `5` → `"0.05"`. Built from the integer, not by `(cents / 100).toFixed(2)`, so no
 * floating-point value is ever in the way. Anything but a positive whole number of cents is a bug in
 * the caller, so it throws rather than sending it.
 */
export function toFiuuAmountParam(cents: number): string {
  if (!Number.isSafeInteger(cents) || cents <= 0) {
    throw new RangeError(`A top-up amount must be a positive whole number of cents, got ${cents}`);
  }
  const ringgit = Math.floor(cents / 100);
  const sen = cents % 100;
  return `${ringgit}.${String(sen).padStart(2, '0')}`;
}

/**
 * Why starting a top-up gave no usable answer — one word per thing the screen says differently.
 *
 * - `signedOut`: the vendor found no customer for the token (`-2`).
 * - `amount`: the vendor refused the amount (`-1`: not a number, not above zero, or under RM 10).
 * - `unavailable`: the vendor could not record the top-up (`0`, a database error): try later.
 * - `network`: the vendor could not be reached, or did not answer in time.
 * - `unusable`: an answer came back but cannot be used — a staff token (`-3`), a code nobody
 *   agreed on, a malformed body, a missing field, a link that is not the gateway, or an amount
 *   other than the one asked for.
 */
export type FiuuTopUpErrorKind = 'signedOut' | 'amount' | 'unavailable' | 'network' | 'unusable';

/**
 * `molpay_pay`'s result code (or the client's own: `NETWORK`, `TIMEOUT`, `HTTP_500`, `MALFORMED`)
 * → the kind.
 */
export function toFiuuTopUpErrorKind(code: string): FiuuTopUpErrorKind {
  switch (code) {
    case '0':
      return 'unavailable';
    case '-1':
      return 'amount';
    case '-2':
      return 'signedOut';
    case 'NETWORK':
    case 'TIMEOUT':
      return 'network';
    default:
      return code.startsWith('HTTP_') ? 'network' : 'unusable';
  }
}

/** Starting a top-up ended without a usable answer. `message` is for the log, not the screen. */
export class FiuuTopUpError extends Error {
  constructor(
    readonly kind: FiuuTopUpErrorKind,
    readonly code: string,
    message: string,
  ) {
    super(`molpay_pay failed (${code}, ${kind}): ${message}`);
    this.name = 'FiuuTopUpError';
  }
}

/**
 * Start a top-up of `amountCents`: the vendor records it and answers with the gateway link.
 * Resolves only to an answer the normaliser accepted — the link is the gateway's and the amount is
 * the one asked for — so the caller can send the customer there without checking again.
 *
 * Every call records a new top-up waiting for payment, so the caller must not call it twice for
 * one press of Pay.
 */
export async function startFiuuTopUp(
  client: LegacyApiClient,
  amountCents: number,
): Promise<FiuuTopUpStart> {
  const amount = toFiuuAmountParam(amountCents);
  const result = await client.call<unknown>('molpay_pay', { amount });
  if (!result.ok) {
    throw new FiuuTopUpError(toFiuuTopUpErrorKind(result.code), result.code, result.message);
  }

  const start = normaliseFiuuTopUpStart(result.data, amountCents);
  if (start === undefined) {
    throw new FiuuTopUpError(
      'unusable',
      'UNUSABLE',
      'The answer had no accdetailid or refid, a different amount, or a link that is not the gateway',
    );
  }
  return start;
}
